import React, { useState, useEffect, useCallback } from 'react';
import { getTheme } from '../theme';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';
import CountPill from './CountPill';

// ── Reviews (25 Oct, customer feedback step 3) ───────────────────────────────
//
// What customers said after a ticket was Done & Paid (server/jobs/
// feedbackRequests.js). "Needs a call" holds every 0 to 6 nobody has followed
// up yet; it is the red count on the sidebar. Logging the call (who, and a
// line about what happened) clears it, so two people do not both ring the
// same customer and nobody is missed.
// Step 4: the NPS figures at the top (30 days, 90 days, 12 months, and by tech
// from 5 answers), and Requests: everything asked or skipped, and why, for
// checking it is working. The score on the ticket is TicketFeedback.jsx.
// Permission `reviews`: Owners and Managers by default.

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders(!!opts.body), ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
  return d;
}

const when = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; } };
const phoneLabel = (p) => { const d = String(p || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p; };
export const scoreTone = (n) => (n <= 6 ? { bg: '#e74c3c', label: 'Unhappy' } : n <= 8 ? { bg: '#e6a23c', label: 'Passive' } : { bg: '#27ae60', label: 'Promoter' });

function Response({ r, T, onLogged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const tone = scoreTone(r.score);
  const low = r.score <= 6;
  const save = async () => {
    setBusy(true); setError('');
    try { const d = await api(`/api/feedback/responses/${r.id}/followup`, { method: 'POST', body: JSON.stringify({ note }) }); onLogged(d.response); setOpen(false); setNote(''); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ background: T.card, border: `1px solid ${low && !r.followup ? 'rgba(231,76,60,0.55)' : T.cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 10, display: 'flex', gap: 16 }}>
      <div title={tone.label} style={{ width: 46, height: 46, borderRadius: 10, background: tone.bg, color: '#fff', fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.score}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{r.name || 'Customer'}</span>
          {r.ticketId && <span style={{ fontSize: 12, color: T.textSub }}>Ticket #{r.ticketId}{r.bike ? ` · ${r.bike}` : ''}{r.tech ? ` · ${r.tech}` : ''}{r.total != null ? ` · $${r.total.toFixed(2)}` : ''}</span>}
          <span style={{ fontSize: 12, color: T.textSub, marginLeft: 'auto' }}>{when(r.answeredAt)}</span>
        </div>
        <div style={{ fontSize: 14, color: r.comment ? T.text : T.textSub, margin: '8px 0', lineHeight: 1.5, fontStyle: r.comment ? 'normal' : 'italic' }}>
          {r.comment ? `“${r.comment}”` : 'No comment.'}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: T.textSub }}>
          {r.phone && <a href={`tel:${r.phone}`} style={{ color: T.text }}>{phoneLabel(r.phone)}</a>}
          {r.email && <a href={`mailto:${r.email}`} style={{ color: T.text }}>{r.email}</a>}
          <span>Asked by {r.channel === 'sms' ? 'text' : 'email'}</span>
          {r.google && <span style={{ color: '#27ae60', fontWeight: 600 }}>Opened the Google review page</span>}
        </div>
        {r.followup && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: T.text, background: T.input, borderRadius: 8, padding: '8px 10px' }}>
            <strong>Followed up</strong> by {r.followup.by}, {when(r.followup.at)}: {r.followup.note}
          </div>
        )}
        {low && !r.followup && !open && (
          <button onClick={() => setOpen(true)} style={{ ...S.btn(true), marginTop: 10, padding: '7px 14px', fontSize: 12.5 }}>Log the call</button>
        )}
        {open && (
          <div style={{ marginTop: 10 }}>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} autoFocus placeholder="What happened? e.g. Called, apologized, booked a free re-adjust for Thursday"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit', resize: 'vertical' }} />
            {error && <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 4 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={save} disabled={busy || !note.trim()} style={{ ...S.btn(true), padding: '7px 14px', fontSize: 12.5, opacity: busy || !note.trim() ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Save'}</button>
              <button onClick={() => { setOpen(false); setError(''); }} style={{ ...S.btn(false), color: T.text, borderColor: T.border, padding: '7px 14px', fontSize: 12.5 }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SKIP_LOOK = { sent: ['Sent', '#3498db'], scheduled: ['Waiting to send', '#8a9ab8'], sending: ['Sending', '#8a9ab8'], skipped: ['Skipped', '#e67e22'], failed: ['Not sent', '#e74c3c'] };
const signed = (n) => (n == null ? '–' : n > 0 ? `+${n}` : String(n));
const PERIOD = { 30: 'Last 30 days', 90: 'Last 90 days', 365: 'Last 12 months' };

function Summary({ T }) {
  const [s, setS] = useState(null);
  useEffect(() => { api('/api/feedback/summary').then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const box = { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: '14px 16px', flex: 1, minWidth: 190 };
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {s.periods.map(p => {
          const total = p.promoters + p.passives + p.detractors;
          const w = (n) => `${total ? (n / total) * 100 : 0}%`;
          return (
            <div key={p.days} style={box}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{PERIOD[p.days]}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 6px' }}>
                <span style={{ fontSize: 30, fontWeight: 700, color: p.nps == null ? T.textSub : p.nps >= 50 ? '#27ae60' : p.nps >= 0 ? T.text : '#e74c3c' }}>{signed(p.nps)}</span>
                <span style={{ fontSize: 12, color: T.textSub }}>NPS</span>
              </div>
              {total > 0 && (
                <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', marginBottom: 6 }} title={`${p.promoters} promoters, ${p.passives} passives, ${p.detractors} unhappy`}>
                  <span style={{ width: w(p.promoters), background: '#27ae60' }} /><span style={{ width: w(p.passives), background: '#e6a23c' }} /><span style={{ width: w(p.detractors), background: '#e74c3c' }} />
                </div>
              )}
              <div style={{ fontSize: 12, color: T.textSub }}>
                {p.asked ? <>{p.answered} answered of {p.asked} asked{p.rate != null ? ` (${p.rate}%)` : ''}</> : 'Nobody asked yet'}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 8, lineHeight: 1.5 }}>
        NPS is the share who scored 9 or 10 minus the share who scored 0 to 6, from −100 to +100. Above 0 is good, above 50 is excellent.
      </div>
      {s.techs.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: T.text, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: T.textSub }}>By tech, last 90 days:</span>
          {s.techs.map(t => (
            <span key={t.name} title={t.nps == null ? `Shown from ${s.techMin} answers, so one bad day does not brand anyone` : ''}
              style={{ border: `1px solid ${T.border}`, borderRadius: 14, padding: '3px 10px' }}>
              {t.name}: <strong>{t.nps == null ? 'not enough yet' : signed(t.nps)}</strong> <span style={{ color: T.textSub }}>({t.answered})</span>
            </span>
          ))}
        </div>
      )}
      {(s.services || []).length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, color: T.textSub, marginBottom: 6 }}>By service, last 90 days. A ticket with two services counts toward both.</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, color: T.text, minWidth: 360 }}>
            <thead><tr>
              {['Service', 'Answers', 'NPS'].map((h, i) => (
                <th key={h} style={{ textAlign: i ? 'right' : 'left', fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px', borderBottom: `1px solid ${T.border}` }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {s.services.map(x => (
                <tr key={x.itemID}>
                  <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.border}` }}>{x.name}</td>
                  <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.border}`, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{x.answered}</td>
                  <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.border}`, textAlign: 'right' }}
                    title={x.nps == null ? `Shown from ${s.techMin} answers` : ''}>
                    {x.nps == null ? <span style={{ color: T.textSub }}>not enough answers yet</span>
                      : <strong style={{ color: x.nps >= 50 ? '#27ae60' : x.nps >= 0 ? T.text : '#e74c3c' }}>{signed(x.nps)}</strong>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Requests({ T }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/feedback/requests?days=30').then(setD).catch(e => setError(e.message)); }, []);
  if (error) return <div style={{ color: '#e74c3c', fontSize: 13 }}>{error}</div>;
  if (!d) return <div style={{ color: T.textSub, fontSize: 13 }}>Loading…</div>;
  if (!d.requests.length) return <div style={{ color: T.textSub, fontSize: 13, padding: '24px 0' }}>Nothing in the last 30 days. Tickets appear here once they are rung up Done &amp; Paid with feedback switched on.</div>;
  const th = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.4, padding: '8px 10px', borderBottom: `1px solid ${T.border}` };
  const td = { fontSize: 12.5, color: T.text, padding: '9px 10px', borderBottom: `1px solid ${T.border}`, verticalAlign: 'top' };
  return (
    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Ticket</th><th style={th}>Customer</th><th style={th}>Rung up</th><th style={th}>What happened</th><th style={th}>Answer</th></tr></thead>
        <tbody>
          {d.requests.map(r => {
            const [label, colour] = SKIP_LOOK[r.status] || [r.status, '#888'];
            return (
              <tr key={r.id}>
                <td style={td}>#{r.ticketId}{r.tech ? <div style={{ fontSize: 11, color: T.textSub }}>{r.tech}</div> : null}</td>
                <td style={td}>{r.name || <span style={{ color: T.textSub }}>No customer</span>}</td>
                <td style={td}>{r.doneAt ? when(r.doneAt) : when(r.createdAt)}</td>
                <td style={td}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10, color: colour, border: `1px solid ${colour}66` }}>{label}</span>
                  <span style={{ marginLeft: 8, color: T.textSub }}>
                    {r.status === 'sent' ? `by ${r.channel === 'sms' ? 'text' : 'email'}, ${when(r.sentAt)}`
                      : r.status === 'scheduled' ? `goes ${when(r.dueAt)}` : r.skipReason}
                  </span>
                </td>
                <td style={td}>{r.score != null ? <strong style={{ color: scoreTone(r.score).bg }}>{r.score}/10</strong> : <span style={{ color: T.textSub }}>{r.status === 'sent' ? 'Not yet' : ''}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Reviews({ theme }) {
  const T = getTheme(theme);
  const [view, setView] = useState(null);   // 'needs' | 'all'; chosen on first load
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (v) => {
    try {
      const d = await api(`/api/feedback/responses?view=${v}`);
      setData(d); setError('');
      return d;
    } catch (e) { setError(e.message); return null; }
  }, []);

  // Opens on "Needs a call" when there is anything in it, otherwise everything.
  useEffect(() => {
    (async () => {
      const d = await load('needs');
      if (d && d.needsCount > 0) setView('needs');
      else { setData(null); setView('all'); load('all'); }
    })();
  }, [load]);

  const pick = (v) => { setView(v); if (v === 'requests') return; setData(d => (d ? { ...d, responses: null } : d)); load(v); };
  const logged = (resp) => {
    setData(d => d && ({
      ...d,
      needsCount: Math.max(0, d.needsCount - 1),
      responses: view === 'needs' ? d.responses.filter(x => x.id !== resp.id) : d.responses.map(x => (x.id === resp.id ? resp : x)),
    }));
    window.dispatchEvent(new Event('spinboard-review-followed-up'));
  };

  const tab = (v, label, n) => (
    <button onClick={() => pick(v)} style={{ ...S.btn(view === v), display: 'inline-flex', alignItems: 'center', gap: 8,
      ...(view === v ? {} : { color: T.text, borderColor: T.border }) }}>
      {label}{n != null && <CountPill n={n} T={T} inverse={view === v} size={11} style={n > 0 && view !== v ? { background: '#e74c3c', color: '#fff' } : {}} />}
    </button>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 980 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 6 }}>Reviews</div>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 16 }}>
        What customers said after their ticket was Done &amp; Paid. A score of 0 to 6 needs a call: the customer was told someone will be in touch. Log the call here so it is not missed and nobody calls twice.
      </div>
      <Summary T={T} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {tab('needs', 'Needs a call', data ? data.needsCount : null)}
        {tab('all', 'All answers')}
        {tab('requests', 'Requests and skips')}
      </div>
      {error && <div style={{ color: '#e74c3c', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {view === 'requests' && <Requests T={T} />}
      {view !== 'requests' && (!data || !data.responses) && !error && <div style={{ color: T.textSub, fontSize: 13 }}>Loading…</div>}
      {view !== 'requests' && data && data.responses && view && data.responses.length === 0 && (
        <div style={{ color: T.textSub, fontSize: 13, padding: '24px 0' }}>
          {view === 'needs' ? 'Nobody is waiting for a call.' : 'No answers yet. They appear here as customers reply.'}
        </div>
      )}
      {view !== 'requests' && data && data.responses && view && data.responses.map(r => <Response key={r.id} r={r} T={T} onLogged={logged} />)}
    </div>
  );
}
